import { useRef, useState } from 'react';
import clsx from 'clsx';
import { Star, Trash2, UploadCloud, GripVertical } from 'lucide-react';

/**
 * Private media gallery manager (products and variants). Controlled by the
 * parent: `media` is the resolved (presigned) array; the callbacks persist each
 * change.
 *
 *   - onUpload(file): upload one image (parent calls the upload endpoint).
 *   - onDelete(storageKey): remove an item.
 *   - onReplace(nextMedia): persist a reordered / re-flagged array.
 *
 * Supports drag-and-drop file upload (drop files on the zone) and drag-and-drop
 * reordering of the thumbnails. The primary image is chosen with the star.
 */
export function GalleryManager({ media = [], onUpload, onDelete, onReplace, busy = false }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [over, setOver] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [dropIdx, setDropIdx] = useState(null);

  const items = [...media].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

  const uploadMany = async (files) => {
    if (!onUpload || !files || !files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        if (file.type && file.type.startsWith('image/')) await onUpload(file);
      }
    } finally {
      setUploading(false);
    }
  };

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    await uploadMany(files);
  };

  const onDropFiles = async (e) => {
    e.preventDefault();
    setOver(false);
    if (dragIdx !== null) return; // a thumbnail reorder, not a file drop
    await uploadMany(Array.from(e.dataTransfer.files || []));
  };

  const setPrimary = (key) => {
    if (!onReplace) return;
    onReplace(items.map((m) => ({ ...m, isPrimary: m.storageKey === key })));
  };

  const commitReorder = (from, to) => {
    if (!onReplace || from === to || from === null || to === null) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onReplace(next.map((m, i) => ({ ...m, sortOrder: i })));
  };

  return (
    <div className="flex flex-col gap-3">
      {/* Upload / drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); if (dragIdx === null) setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={onDropFiles}
        onClick={() => fileRef.current && fileRef.current.click()}
        className={clsx(
          'flex cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed px-4 py-6 text-center transition-colors',
          over ? 'border-accent bg-accent/5' : 'border-border hover:border-border-strong hover:bg-surface-subtle',
          (busy || uploading) && 'pointer-events-none opacity-60'
        )}
      >
        <UploadCloud className="h-6 w-6 text-ink-muted" />
        <p className="text-sm text-ink">{uploading ? 'Caricamento...' : 'Trascina qui le immagini o clicca per sceglierle'}</p>
        <p className="text-xs text-ink-faint">JPG, PNG, WebP, GIF, AVIF, max 10MB</p>
        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={onPick} />
      </div>

      {items.length === 0 ? (
        <p className="text-sm text-ink-faint">Nessuna immagine. La prima che imposti come principale sarà la copertina.</p>
      ) : (
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {items.map((m, idx) => (
            <li
              key={m.storageKey || m.url || idx}
              draggable
              onDragStart={() => setDragIdx(idx)}
              onDragEnter={() => setDropIdx(idx)}
              onDragEnd={() => { commitReorder(dragIdx, dropIdx); setDragIdx(null); setDropIdx(null); }}
              onDragOver={(e) => e.preventDefault()}
              className={clsx(
                'group relative overflow-hidden rounded-lg border bg-surface transition-shadow',
                m.isPrimary ? 'border-accent ring-1 ring-accent' : 'border-border',
                dragIdx === idx && 'opacity-50',
                dropIdx === idx && dragIdx !== null && dragIdx !== idx && 'ring-2 ring-accent'
              )}
            >
              <div className="relative aspect-square w-full bg-surface-subtle">
                {m.url ? <img src={m.url} alt={m.alt || ''} className="h-full w-full object-cover" draggable={false} /> : null}
                <span className="absolute left-1 top-1 rounded bg-black/40 p-0.5 text-white opacity-0 transition-opacity group-hover:opacity-100" title="Trascina per riordinare">
                  <GripVertical className="h-3.5 w-3.5" />
                </span>
                {m.isPrimary && (
                  <span className="absolute right-1 top-1 inline-flex items-center gap-1 rounded bg-accent px-1.5 py-0.5 text-[10px] font-medium text-white">
                    <Star className="h-3 w-3 fill-current" /> Principale
                  </span>
                )}
              </div>
              <div className="flex items-center justify-between gap-1 px-1.5 py-1.5">
                <button
                  type="button"
                  onClick={() => setPrimary(m.storageKey)}
                  disabled={busy || m.isPrimary}
                  className={clsx('inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px]', m.isPrimary ? 'text-accent' : 'text-ink-muted hover:text-ink', 'disabled:opacity-60')}
                  title="Imposta come principale"
                >
                  <Star className={clsx('h-3.5 w-3.5', m.isPrimary && 'fill-current')} /> {m.isPrimary ? 'Principale' : 'Rendi principale'}
                </button>
                <button type="button" title="Elimina" onClick={() => onDelete && onDelete(m.storageKey)} disabled={busy} className="rounded p-1 text-ink-muted hover:text-danger disabled:opacity-30">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
